<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { DateTime, Duration } from 'luxon'
  import { useEventBus } from '@vueuse/core'

  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import type { ListDescriptionItemType } from '@/elements/ListDescriptionItem.vue'

  import { apiWorktimeTrackingSummaryGet, apiWorktimeTrackingSummaryPost, EmployeeStateDefaultReadReadable, type WorktimeTrackingDayVersionEntryJsonldDefaultRead, WorktimeTrackingSummaryJsonldReadable } from '@/api'
  import { BreakStartIcon, BreakStopIcon, HomeIcon, WorkStopIcon } from '@/config/icons'
  import { useFormatter } from '@/composables/useFormatter.ts'
  import { useGeneratedEmployeeStateStore, useGeneratedEmployeeStore } from '@/stores'

  import { useToast } from 'primevue/usetoast'
  import SplitButton from 'primevue/splitbutton'
  import InlineMessage from 'primevue/inlinemessage'
  import { useI18n } from 'vue-i18n'
  import type { MenuItem } from 'primevue/menuitem'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import ListDescription from '@/elements/ListDescription.vue'

  const $formatter = useFormatter()
  const toastService = useToast()
  const titrop = useTitrop()
  const stateStore = useGeneratedEmployeeStateStore()
  const employeeStore = useGeneratedEmployeeStore()

  const { t } = useI18n()
  let liveTickerInterval: number | undefined = undefined
  let synchronizeInterval: number | undefined = undefined
  const workTime = ref<Duration>()
  const breakTime = ref<Duration>()

  const isLoading = ref(true)
  const renderKey = ref<number>(0)
  const isBlocked = ref<boolean>(false)

  const translationDict = {
    'WORKING': t('WORKING'),
    'OFFDUTY': t('OFFDUTY'),
    'BREAK': t('BREAK')
  }

  const lastTrackingEntry = computed(() => daySummary.value.currentVersion?.entries?.at(-1))
  const isPOSTAllowed = computed(
    () =>
      !allStates.value
        .filter((s) => s.code === 'I' || s.code === 'V')
        .map((s) => s['@id'])
        .includes(titrop.value.user?.current?.current_state!)
  )

  const daySummary = ref<WorktimeTrackingSummaryJsonldReadable>({})
  const allStates = ref<EmployeeStateDefaultReadReadable[]>([])

  async function refresh() {
    daySummary.value = (
      await apiWorktimeTrackingSummaryGet<true>({
        query: {
          date: DateTime.now().toISO({ includeOffset: false })
        }
      })
    ).data
  }

  onMounted(async () => {
    isLoading.value = true
    await refresh()
    allStates.value = (await stateStore.findAll(null)).value
    isLoading.value = false
    synchronizeInterval = window.setInterval(() => {
      void refresh()
    }, 60000)

    //let workTime = Duration.fromDurationLike({ second: getBookedTime() - getBreakTime() }) //Duration.fromISO(daySummary.value?.times?.WORKING ?? 'PT0H')
    workTime.value = Duration.fromDurationLike({ second: getBookedTime() - getBreakTime() })
    breakTime.value = Duration.fromDurationLike({ second: getBreakTime() }) //Duration.fromISO(daySummary.value?.times?.BREAK ?? 'PT0H')
    liveTickerInterval = window.setInterval(() => {
      if (lastTrackingEntry.value?.action == 'WORK_BEGIN' || lastTrackingEntry.value?.action == 'BREAK_END') {
        //const sumCurrent = DateTime.now().diff(DateTime.fromISO(lastTrackingEntry.value.timestamp!))
        workTime.value = workTime.value?.plus({ seconds: 1 } /*sumCurrent*/)
      }

      if (lastTrackingEntry.value?.action == 'BREAK_BEGIN') {
        //const sumCurrent = DateTime.now().diff(DateTime.fromISO(lastTrackingEntry.value.timestamp!))
        breakTime.value = breakTime.value?.plus({ seconds: 1 } /*sumCurrent*/)
      }

      listContent.value = [
        {
          title: t('workingTime'),
          description: $formatter.Time(workTime.value, { withSeconds: true, suppressDays: true })
        },
        {
          title: t('breakTime'),
          description: $formatter.Time(breakTime.value, { withSeconds: true, suppressDays: true })
        }
      ]
      renderKey.value++
    }, 1000)
  })
  onUnmounted(() => {
    if (liveTickerInterval !== undefined) window.clearInterval(liveTickerInterval)
    if (synchronizeInterval !== undefined) window.clearInterval(synchronizeInterval)
  })

  const postAction = async (actionType: WorktimeTrackingDayVersionEntryJsonldDefaultRead['action']) => {
    if (titrop.value.user?.current?.current_state === 'BREAK_BEGIN' && actionType === 'WORK_END')
      void apiWorktimeTrackingSummaryPost<true>({ body: { action: actionType } }).then((resp) => {
        isBlocked.value = true
        setTimeout(function() {
          daySummary.value = resp.data
          isBlocked.value = false
        }, 500)
      })
    else daySummary.value = (await apiWorktimeTrackingSummaryPost<true>({ body: { action: actionType } })).data
    useEventBus('worktime_action_changed').emit()
    toastService.add({
      summary: 'Die Stempelung wurde erfasst!',
      life: 2500,
      severity: 'success'
    })

    renderKey.value++
  }

  const postStateChange = async (stateCode: string) => {
    await employeeStore.putId({
      path: { id: titrop.value.user.currentIri },
      body: { current_state: allStates.value.find((s) => s.code === stateCode)?.['@id'] }
    })
  }

  const workBeginPost = async (code: string) => {
    await postAction('WORK_BEGIN')
    await postStateChange(code)
    useEventBus('work_begin_post').emit()
  }

  const listContent = ref<(ListDescriptionItemType | undefined)[]>()

  const getCurrentState = () => {
    return allStates.value.find((s) => s['@id'] === titrop.value.user?.current?.current_state)?.title
  }

  const getBookedTime = () => {
    let res = 0
    const version = daySummary.value.currentVersion
    const workBegins = version?.entries?.filter((e) => e.action === 'WORK_BEGIN') ?? []
    const workEnds = version?.entries?.filter((e) => e.action === 'WORK_END') ?? []
    if (workBegins.length > workEnds.length) workEnds.push({ timestamp: DateTime.now().toISO({ suppressMilliseconds: true }) })
    for (let i = 0; i < workBegins.length; i++) res += DateTime.fromISO(workEnds[i].timestamp!).diff(DateTime.fromISO(workBegins[i].timestamp!)).as('seconds')
    return res
  }

  const getBreakTime = () => {
    let res = 0
    const version = daySummary.value.currentVersion
    const breakBegins = version?.entries?.filter((e) => e.action === 'BREAK_BEGIN') ?? []
    const breakEnds = version?.entries?.filter((e) => e.action === 'BREAK_END') ?? []
    if (breakBegins.length > breakEnds.length) breakEnds.push({ timestamp: DateTime.now().toISO({ suppressMilliseconds: true }) })
    for (let i = 0; i < breakBegins.length; i++) res += DateTime.fromISO(breakEnds[i].timestamp!).diff(DateTime.fromISO(breakBegins[i].timestamp!)).as('seconds')
    return res
  }

  useEventBus('state_change_and_day_end_post').on(() => {
    void refresh().then(() => {
      renderKey.value++
    })
  })

  useEventBus('WTT_correction_gadget_data_change').on(async () => {
    await refresh().then(() => {
      workTime.value = Duration.fromDurationLike({ second: getBookedTime() - getBreakTime() })
      breakTime.value = Duration.fromDurationLike({ second: getBreakTime() })
      renderKey.value++
    })
  })

  interface SplitButtonType {
    label: string
    icon: AllIconsType
    command: () => Promise<void>
  }

  const confDefaultWorktimeType = titrop.value.config.user<'office' | 'homeoffice'>('default_worktime_type', 'office')

  const buttonHomeOffice: SplitButtonType = {
    label: t('beginHome'),
    icon: 'HomeIcon',
    command: async () => workBeginPost('H')
  }
  const buttonOffice: SplitButtonType = {
    label: t('beginOffice'),
    icon: 'OfficeIcon',
    command: async () => workBeginPost('A')
  }

  const buttonDefaultComp = computed(() => {
    if (confDefaultWorktimeType.value == 'homeoffice') return buttonHomeOffice
    else return buttonOffice
  })
  const items: SplitButtonType[] = [confDefaultWorktimeType.value == 'homeoffice' ? buttonOffice : buttonHomeOffice]
</script>

<template>
  <div class="max-w-full @container/wttTrackingWidget">
    <WidgetSkeleton v-if="isLoading || !daySummary" variant="worktime" />
    <div
      v-else
      class="text-center">
      <span class="text-sm mb-1">{{ t('currentState', { state: translationDict[daySummary?.currentState!] }) }}</span>
      <InlineMessage
        v-if="!isPOSTAllowed"
        class="w-full mb-2">
        {{ t('forbidden', { state: getCurrentState() }) }}
      </InlineMessage>
      <!--            <ButtonGroup class="grid grid-cols-2 gap-2 mt-2">-->
      <div class="grid grid-cols-1 @md/wttTrackingWidget:grid-cols-2 gap-1 mt-1">
        <!--            <div class="flex flex-col gap-2 mt-2">-->
        <SplitButton
          :button-props="{ class: 'flex-auto' }"
          :disabled="!daySummary.actions?.includes('WORK_BEGIN') || !isPOSTAllowed || isBlocked"
          :model="items as unknown as MenuItem[]"
          :outlined="!daySummary.actions?.includes('WORK_BEGIN') || !isPOSTAllowed || isBlocked"
          class="w-full text-sm font-semibold rounded"
          size="small"
          @click="buttonDefaultComp.command">
          <div class="flex items-center justify-center">
            <component
              :is="buttonDefaultComp.icon"
              class="mr-2" />
            <span>{{ buttonDefaultComp.label }}</span>
          </div>
          <!--          <span class="text-sm font-semibold">{{ buttonDefaultComp.label }}</span>-->
          <template #item="data">
            <ButtonDefault
              :icon="(data.item as SplitButtonType).icon"
              :label="(data.item as SplitButtonType).label"
              class="flex items-center flex-auto"
              fluid
              severity="secondary"
              size="small" />
          </template>
        </SplitButton>

        <ButtonDefault
          :key="renderKey"
          :disabled="!daySummary.actions?.includes('BREAK_BEGIN') || !isPOSTAllowed"
          :icon="BreakStartIcon"
          :outlined="!daySummary.actions?.includes('BREAK_BEGIN') || !isPOSTAllowed"
          class="w-full text-sm font-semibold rounded"
          :label="t('beginBreak')"
          size="small"
          @click="postAction('BREAK_BEGIN')" />

        <ButtonDefault
          :key="renderKey"
          :disabled="!daySummary.actions?.includes('BREAK_END') || !isPOSTAllowed"
          :icon="BreakStopIcon"
          :outlined="!daySummary.actions?.includes('BREAK_END') || !isPOSTAllowed"
          class="w-full text-sm font-semibold rounded"
          :label="t('endBreak')"
          size="small"
          @click="postAction('BREAK_END')" />

        <ButtonDefault
          :key="renderKey"
          :disabled="!daySummary.actions?.includes('WORK_END') || !isPOSTAllowed"
          :icon="WorkStopIcon"
          :outlined="!daySummary.actions?.includes('WORK_END') || !isPOSTAllowed"
          class="w-full text-sm font-semibold rounded"
          :label="t('endWork')"
          size="small"
          @click="postAction('WORK_END')" />
      </div>
      <ListDescription class="mt-2 p-0" :items="listContent" small :defaultColsNumber="2" />
      <!--      <div class="w-full text-sm space-y-1 mt-2">
              <div v-for="(line, index) in listContent" class="grid grid-cols-2">
                <div class="text-left">{{ line?.title }}</div>
                <div class="text-right">{{ line?.description }}</div>

                <Divider
                  v-if="index < listContent?.length - 1"
                  class="col-span-2 my-1 mx-0"
                />
              </div>
            </div>-->
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "WORKING": "Am arbeiten",
    "OFFDUTY": "Außer Dienst",
    "BREAK": "Pause",
    "workingTime": "Arbeitszeit:",
    "breakTime": "Pausenzeit:",
    "beginHome": "Arbeit beginnen (Home)",
    "beginOffice": "Arbeit beginnen (Büro)",
    "currentState": "Aktueller Zustand: {state}",
    "forbidden": "Arbeitszeiterfassung ist verboten. Ihr Status: {state}",
    "beginBreak": "Pause beginnen",
    "endBreak": "Pause beenden",
    "endWork": "Arbeit beenden"
  },
  "en": {
    "WORKING": "Working",
    "OFFDUTY": "Off duty",
    "BREAK": "Break",
    "workingTime": "Working time:",
    "breakTime": "Break time:",
    "beginHome": "Start work (home)",
    "beginOffice": "Start work (office)",
    "currentState": "Current state: {state}",
    "forbidden": "Work time tracking is not allowed. Your status: {state}",
    "beginBreak": "Start break",
    "endBreak": "End break",
    "endWork": "End work"
  }
}
</i18n>
